"use client"

import {useEffect, useMemo, useRef, useState} from "react"
import "./AudioLevel.scss"
import MediaDevicesUtils from "../../utils/MediaDevicesUtils";

type AudioLevelProps = {
    deviceId: string | null
    gain: number
    // stream: MediaStream | null
    minThreshold?: number // Threshold below which the level is considered too low (red)
    className?: string
}

/**
 * A small bar that track the audio level for input devices.
 *
 * @param deviceId the id of the device that will be tracked
 * @param minThreshold
 * @param className
 * @param onAudioLevelAPIReady
 */
export const AudioLevel = ({deviceId, gain, minThreshold = 15, className = ""}: AudioLevelProps) => {
    const [inputLevel, setInputLevel] = useState<number>(0)
    const audioContextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
    const animationRef = useRef<number | null>(null)
    const currentStreamRef = useRef<MediaStream | null>(null)

    const emaRef = useRef<number>(0);
    // Prevents double-triggering reconnect when the stream dies
    const reconnectScheduledRef = useRef<boolean>(false);

   //console.log('gain: ', gain);
    const [reconnectionWatchDog, setReconnectionWatchDog] = useState<boolean>(false);

    const onMediaDeviceSelected = useMemo(() => {
        return (e: MessageEvent<any>) => {
            if (e.data?.type === 'mediadevice-selected') {
                //console.debug('AudioLevel - mediadevice-selected', e.data.type, deviceId);
                if (e.data.deviceId === deviceId) {
                    setReconnectionWatchDog((prevState) => !prevState)
                }
            }
        }
    }, [deviceId]);

    useEffect(() => {
        if (!deviceId) return;
        // Skip initial mount — Effect 2 handles setup
        if (!currentStreamRef.current) return;

        MediaDevicesUtils.instance().getInputDeviceStream(deviceId, gain)
            .then(mediaStream => {
                // If the stream was replaced (e.g. dead stream recreated), trigger reconnection
                if (mediaStream !== currentStreamRef.current) {
                    setReconnectionWatchDog(prev => !prev);
                }
            })
            .catch(err => console.error('AudioLevel gain update failed', err));
    }, [deviceId, gain]);

    useEffect(() => {
        // Clean up previous audio processing if it exists
        if (audioContextRef.current) {
            stopAudioProcessing()
        }

        if (!deviceId) return

        MediaDevicesUtils.instance().getInputDeviceStream(deviceId, gain)
            .then(mediaStream => {
                currentStreamRef.current = mediaStream;
                setupAudioProcessing(mediaStream)
            }).catch((err) => {
            console.error(`AudioLevel - dId: ${deviceId} - ${err.name}: ${err.message}`)
        })

        window.addEventListener('message', onMediaDeviceSelected);

        // Cleanup on unmount or when stream changes
        return () => {
            stopAudioProcessing()
            currentStreamRef.current = null;
        }

    }, [deviceId, reconnectionWatchDog]);

    const setupAudioProcessing = async (mediaStream: MediaStream) => {
        try {
            // Create audio context and analyzer
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

            // Ensure it's running
            if (audioContext.state === 'suspended') {
                try { await audioContext.resume(); } catch {}
            }

            const analyser = audioContext.createAnalyser()
            // analyser.fftSize = 128 // match old component - before 512
            // analyser.smoothingTimeConstant = 0.8

            analyser.fftSize = 1024;           // better time resolution than 128
            analyser.smoothingTimeConstant = 0.5; // smoothing on FFT (less relevant for time-domain, but ok)

            // Connect the stream to the analyzer
            const source = audioContext.createMediaStreamSource(mediaStream)
            source.connect(analyser)

            // Store references
            audioContextRef.current = audioContext
            analyserRef.current = analyser
            sourceRef.current = source

            // Start updating the audio level
            updateAudioLevel()

            // const micAudioContext = createAudioContext(deviceId, 'audioinput') as AudioContext;
            // audioLevelAPI.setStreamToMonitor(micAudioContext, mediaStream);

        } catch (err) {
            console.error("Error processing audio stream:", err)
        }
    }

    const stopAudioProcessing = () => {
        // Stop the animation frame
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current)
            animationRef.current = null
        }

        // Clean up audio context
        if (sourceRef.current) {
            sourceRef.current.disconnect()
            sourceRef.current = null
        }

        if (audioContextRef.current && audioContextRef.current.state !== "closed") {
            audioContextRef.current.close()
            audioContextRef.current = null
        }

        analyserRef.current = null
        setInputLevel(0)

        window.removeEventListener('message', onMediaDeviceSelected)

        // Reset the reconnect gate so the next stream can trigger reconnect if it also dies
        reconnectScheduledRef.current = false
    }

    const updateAudioLevel = () => {
        if (!analyserRef.current) return

        const bufferLength = analyserRef.current.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)

        const updateLevel = () => {
            if (!analyserRef.current) return

            // Detect when the underlying stream is freed externally (e.g. changeInputLanguage
            // calls freeAudioHandler → handler.stop() kills the tracks). Checking readyState
            // each frame is more reliable than the 'ended' event which may not fire after
            // AudioContext.close() precedes track.stop() in AudioInputHandler.stop().
            if (currentStreamRef.current && !reconnectScheduledRef.current) {
                const hasLiveTracks = currentStreamRef.current.getAudioTracks()
                    .some(t => t.readyState === 'live')
                if (!hasLiveTracks) {
                    reconnectScheduledRef.current = true
                    setReconnectionWatchDog(prev => !prev)
                    return
                }
            }

            // Get frequency data
            analyserRef.current.getByteFrequencyData(dataArray)

            // Calculate average level
            let sum = 0
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i]
            }
            const average = sum / bufferLength

            /// v1- OLD VERSION
            const audioLevelThreshold = 60 // same as MediaStreamMonitor
            const percentage = average > audioLevelThreshold ? 100
                : Math.round((average * 100) / audioLevelThreshold)

            setInputLevel(percentage);
            //console.log('percentage v1 vs v2 vs vD: ', percentagev1, percentage);

            animationRef.current = requestAnimationFrame(updateLevel)
        }

        animationRef.current = requestAnimationFrame(updateLevel)
    }

    // Determine the level status based on the threshold
    const getLevelStatus = () => {
        // if (inputLevel < minThreshold) return "low"
        return "good"
    }

    const levelStatus = getLevelStatus()

    return (
        <div className={`audio-level ${className}`}>
            <div className="audio-level-meter">
                <div className={`audio-level-fill ${levelStatus}`}
                     // style={{width: `${inputLevel}%`}}
                     style={{ width: `${Math.max(0, inputLevel)}%` }}
                />
            </div>
        </div>
    )
}
